import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "./useAuth";
import {
  type UserStage,
  type CompanionMessage,
  type DiscoveryCelebration,
  getCompanionMessageConfig,
  discoveryCelebrations,
} from "../lib/companionGuide";

const STORAGE_KEY_DISMISSED = "companion_dismissed";
const STORAGE_KEY_DISCOVERIES = "companion_discoveries";
const STORAGE_KEY_FIRST_LOGIN_SEEN = "companion_first_login_seen";

// ─── Types ───────────────────────────────────────────────────────────────────

interface UseCompanionGuideReturn {
  messages: CompanionMessage[];
  dismissMessage: (id: string) => void;
  userStage: UserStage;
  discoveries: Record<string, number>;
  userName: string;
  pendingDiscovery: DiscoveryCelebration | null;
  clearDiscovery: () => void;
  triggerCelebration: (eventKey: string) => void;
  showFirstLogin: boolean;
  dismissFirstLogin: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getDismissed(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_DISMISSED);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function saveDismissed(dismissed: Set<string>) {
  localStorage.setItem(STORAGE_KEY_DISMISSED, JSON.stringify(Array.from(dismissed)));
}

function getDiscoveries(): Record<string, number> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_DISCOVERIES);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveDiscoveries(discoveries: Record<string, number>) {
  localStorage.setItem(STORAGE_KEY_DISCOVERIES, JSON.stringify(discoveries));
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useCompanionGuide(
  routeKey: string,
  processCount: number,
  hasSpaces: boolean,
  hasInsights: boolean
): UseCompanionGuideReturn {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [dismissed, setDismissed] = useState<Set<string>>(getDismissed);
  const [discoveries, setDiscoveries] = useState<Record<string, number>>(
    getDiscoveries
  );
  const [pendingDiscovery, setPendingDiscovery] =
    useState<DiscoveryCelebration | null>(null);
  const [showFirstLogin, setShowFirstLogin] = useState(false);

  // User display name
  const userName = useMemo(() => {
    if (!user) return "";
    if (user.displayName) return user.displayName;
    const parts = [user.firstName, user.lastName].filter(Boolean);
    return parts.join(" ");
  }, [user]);

  // Determine user stage
  const userStage: UserStage = useMemo(() => {
    if (processCount >= 5 && hasInsights) return "active";
    if (processCount >= 3 || hasInsights) return "engaged";
    if (processCount >= 1) return "started";
    if (dismissed.size > 0 || Object.keys(discoveries).length >= 2)
      return "exploring";
    return "brand_new";
  }, [processCount, hasInsights, dismissed.size, discoveries]);

  // Check first login
  useEffect(() => {
    if (!user?.email) return;
    const seen = localStorage.getItem(STORAGE_KEY_FIRST_LOGIN_SEEN);
    if (!seen && userStage === "brand_new") {
      setShowFirstLogin(true);
    }
  }, [user?.email, userStage]);

  // Discovery tracking — check if this is the first time visiting this route
  useEffect(() => {
    if (!routeKey || userStage !== "brand_new" && userStage !== "exploring")
      return;

    const celebrationConfig = discoveryCelebrations[routeKey];
    if (!celebrationConfig) return;

    if (!discoveries[routeKey]) {
      const updated = { ...discoveries, [routeKey]: Date.now() };
      setDiscoveries(updated);
      saveDiscoveries(updated);
      // Resolve the translation key for the celebration message
      setPendingDiscovery({
        ...celebrationConfig,
        message: t(celebrationConfig.messageKey),
      });
    }
  }, [routeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get companion messages for current route — resolve i18n keys
  const messages: CompanionMessage[] = useMemo(() => {
    const result: CompanionMessage[] = [];

    const config = getCompanionMessageConfig(routeKey as any, userStage);
    if (config && !dismissed.has(config.id)) {
      result.push({
        ...config,
        title: config.titleKey ? t(config.titleKey, { name: userName }) : "",
        message: t(config.messageKey, { name: userName }),
        action: config.action
          ? { ...config.action, labelKey: config.action.labelKey, label: t(config.action.labelKey), route: config.action.route }
          : undefined,
      } as CompanionMessage);
    }

    return result;
  }, [routeKey, userStage, dismissed, userName, t]);

  // Dismiss a companion message
  const dismissMessage = useCallback((id: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveDismissed(next);
      return next;
    });
  }, []);

  // Trigger a celebration for an event (not route-based)
  const triggerCelebration = useCallback((eventKey: string) => {
    const celebrationConfig = discoveryCelebrations[eventKey];
    if (!celebrationConfig) return;
    if (discoveries[eventKey]) return; // already celebrated
    const updated = { ...discoveries, [eventKey]: Date.now() };
    setDiscoveries(updated);
    saveDiscoveries(updated);
    setPendingDiscovery({
      ...celebrationConfig,
      message: t(celebrationConfig.messageKey),
    });
  }, [discoveries, t]);

  // Clear pending discovery celebration
  const clearDiscovery = useCallback(() => {
    setPendingDiscovery(null);
  }, []);

  // Dismiss first login
  const dismissFirstLogin = useCallback(() => {
    setShowFirstLogin(false);
    localStorage.setItem(STORAGE_KEY_FIRST_LOGIN_SEEN, "true");
  }, []);

  return {
    messages,
    dismissMessage,
    userStage,
    discoveries,
    userName,
    pendingDiscovery,
    clearDiscovery,
    triggerCelebration,
    showFirstLogin,
    dismissFirstLogin,
  };
}
